import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {axiosInstance} from "../../shared/api/axiosInstance";
import {
    CreateProjectPayload,
    CurrentProject, DocumentData, FetchProjectPayload, FetchProjectResponse, FetchProjectResult, ImageData,
    ProjectData, ProjectResponse,
    ProjectState,
    UpdateProjectPayload, UploadDocumentPayload, UploadImagePayload
} from "../../shared/types/projectTypes";
import axios, {AxiosError} from "axios";
import {ScriptData} from "../../shared/types/scriptTypes";


export const createProject = createAsyncThunk<
    CurrentProject,
    CreateProjectPayload,
    { rejectValue: string }
>(
    'project/createProject',
    async (projectDetails, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post<ProjectResponse>(
                '/projects',
                {
                    data: {
                        type: 'projects',
                        attributes: {
                            name: projectDetails.name,
                            category: projectDetails.category,
                            industry: projectDetails.industry,
                            content_type: projectDetails.content_type,
                            goal: projectDetails.goal,
                            tone: projectDetails.tone,
                        },
                    },
                }
            );

            const { data } = response.data;

            const transformedData: CurrentProject = {
                id: data.id,
                name: data.attributes.name,
                category: data.attributes.category,
                industry: data.attributes.industry,
                content_type: data.attributes.content_type,
                goal: data.attributes.goal,
                tone: data.attributes.tone,

            };

            console.log('Transformed Data', transformedData);
            return transformedData;

        } catch (error) {
            console.log('Error occurred while creating the project');
            if (axios.isAxiosError(error) && error.response) {
                return rejectWithValue(error.response.data.message);
            }
            return rejectWithValue('An unexpected error occurred');
        }
    }
);


export const updateProject = createAsyncThunk<
    CurrentProject,
    UpdateProjectPayload,
    { rejectValue: string }
>(
    'project/updateProject',
    async (projectDetails, { rejectWithValue }) => {

        try {
            console.log({data: {
                    type: 'projects',
                    id: projectDetails.id,
                    attributes: {
                        ...(projectDetails.name && { name: projectDetails.name }),
                        ...(projectDetails.category && { category: projectDetails.category }),
                        ...(projectDetails.industry && { industry: projectDetails.industry }),
                        ...(projectDetails.content_type && { content_type: projectDetails.content_type }),
                        ...(projectDetails.goal && { goal: projectDetails.goal }),
                        ...(projectDetails.tone && { tone: projectDetails.tone }),
                        ...(projectDetails.link && { link: projectDetails.link }),
                        ...(projectDetails.voice && { voice: projectDetails.voice }),
                    },
                    relationships: projectDetails.image
                        ? {
                            image: {
                                type: 'images',
                                id: projectDetails.image.id,

                            },
                        }
                        :  {
                        image: null
                        },
                }});
            const response = await axiosInstance.patch<ProjectResponse>(
                `/projects/${projectDetails.id}`,
                {
                    data: {
                        type: 'projects',
                        id: projectDetails.id,
                        attributes: {
                            ...(projectDetails.name && { name: projectDetails.name }),
                            ...(projectDetails.category && { category: projectDetails.category }),
                            ...(projectDetails.industry && { industry: projectDetails.industry }),
                            ...(projectDetails.content_type && { content_type: projectDetails.content_type }),
                            ...(projectDetails.goal && { goal: projectDetails.goal }),
                            ...(projectDetails.tone && { tone: projectDetails.tone }),
                            ...(projectDetails.link && { link: projectDetails.link }),
                            ...(projectDetails.voice && { voice: projectDetails.voice }),
                        },
                        relationships: projectDetails.image
                            ? {
                                image: {
                                    type: 'images',
                                    id: projectDetails.image.id,

                                },
                            }
                            :  {
                                image: null
                            },
                    },
                }
            );

            const { data,included } = response.data;
            const imageItem = included?.find((item) => item.type === 'images');
            const image = imageItem?.attributes as {
                id: string;
                url: string;
            };

            const transformedData: CurrentProject = {
                id: data.id,
                name: data.attributes.name,
                category: data.attributes.category,
                industry: data.attributes.industry || [],
                content_type: data.attributes.content_type,
                goal: data.attributes.goal,
                tone: data.attributes.tone,
                link: data.attributes.link,
                voice:data.attributes.voice,
                image:image
            };

            return transformedData;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const axiosError = error as AxiosError<{ message: string }>;
                return rejectWithValue(axiosError.response?.data.message || 'An error occurred while updating the project');
            }
            return rejectWithValue('An unexpected error occurred');
        }
    }
);
export const uploadDocument = createAsyncThunk<
    DocumentData,
    UploadDocumentPayload,
    { rejectValue: string }
>(
    'documents/uploadDocument',
    async ({ file }, { rejectWithValue }) => {
        try {

            const formData = new FormData();
            formData.append('file', file);

            const response = await axiosInstance.post<{ data: DocumentData }>(
                '/documents',
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );


            return response.data.data;
        } catch (error) {
            console.log('Error occurred while uploading the document');
            if (axios.isAxiosError(error) && error.response) {
                const axiosError = error as AxiosError<{ message: string }>;
                return rejectWithValue(axiosError.response?.data.message || 'Failed to upload document');
            }
            return rejectWithValue('An unexpected error occurred');
        }
    }
);

export const uploadImage = createAsyncThunk<
    ImageData,
    UploadImagePayload,
    { rejectValue: string }
>(
    'images/uploadImage',
    async ({ file }, { rejectWithValue }) => {
        try {
            console.log('UPDATE image')
            const formData = new FormData();
            formData.append('file', file);

            const response = await axiosInstance.post<{ data: any }>(
                '/images',
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }


            );
            const { data } = response.data;
            return {id:data.id,url:data.attributes.url}
        } catch (error) {
            console.log('Error occurred while uploading the image');
            if (axios.isAxiosError(error) && error.response) {
                const axiosError = error as AxiosError<{ message: string }>;
                return rejectWithValue(axiosError.response?.data.message || 'Failed to upload image');
            }
            return rejectWithValue('An unexpected error occurred');
        }
    }
);
export const fetchProject = createAsyncThunk<
    FetchProjectResult,
    FetchProjectPayload,
    { rejectValue: string }
>(
    'project/fetchProject',
    async ({ projectId }, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get<FetchProjectResponse>(
                `/projects/${projectId}`
            );

            const { data, included } = response.data;

            const scriptItem = included?.find((item) => item.type === 'script_texts');
            const imageItem = included?.find((item) => item.type === 'images');
            console.log('INCLUDED',included)
            const script = scriptItem?.attributes as {
                title: string;
                preview: string;
                body: string;
            };
            const image = imageItem?.attributes as {
                id: string;
                url: string;
            };

            const transformedData: CurrentProject = {
                id: data.id,
                name: data.attributes.name,
                category: data.attributes.category,
                industry: data.attributes.industry || [],
                content_type: data.attributes.content_type,
                goal: data.attributes.goal,
                tone: data.attributes.tone,
                link: data.attributes.link,
                voice: data.attributes.voice,
                image: image,
            };

            return { project: transformedData, script };
        } catch (error) {
            if (axios.isAxiosError(error) && error.response) {
                const axiosError = error as AxiosError<{ message: string }>;
                return rejectWithValue(axiosError.response?.data.message || 'Failed to fetch project');
            }
            return rejectWithValue('An unexpected error occurred');
        }
    }
);


export const generateScript = createAsyncThunk<
    ScriptData,
    { projectId: string },
    { rejectValue: string }
>(
    'scripts/generateScript',
    async ({ projectId }, { rejectWithValue }) => {
        try {

            const response = await axiosInstance.post<any>(
                `/projects/${projectId}/script_texts`
            );


            console.log(response)
            const {data} = response.data;
            console.log(data)
            const transformedData: ScriptData = {

                title: data.attributes.title,
                preview: data.attributes.preview,
                body: data.attributes.body,
            };

            return transformedData
        } catch (error: any) {

            if (axios.isAxiosError(error) && error.response) {
                return rejectWithValue(error.response?.data.message || 'Failed to generate script');
            }
            return rejectWithValue('An unexpected error occurred');
        }
    }
);
export const fetchImage = createAsyncThunk<
    ImageData,
    string,
    { rejectValue: string }
>(
    'images/fetchImage',
    async (imageId, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get<{ data: { id: string; attributes: { url: string } } }>(
                `/images/${imageId}`
            );
            const { id, attributes } = response.data.data;

            return { id, url: attributes.url };
        } catch (error) {
            if (axios.isAxiosError(error) && error.response) {
                return rejectWithValue(error.response?.data.message || 'Failed to fetch image');
            }
            return rejectWithValue('An unexpected error occurred');
        }
    }
);
export const generateImage = createAsyncThunk<
    ImageData,
    { projectId: string; prompt: string },
    { rejectValue: string }
>(
    'images/generateImage',
    async ({ projectId, prompt }, { rejectWithValue }) => {
        try {

            const response = await axiosInstance.put<{ data: { id: string; attributes: { url: string } } }>(
                `/projects/${projectId}/image/generate`,
                {
                    data: {
                        type: "images",
                        attributes: {
                            prompt,
                        },
                    },
                }
            );

            const { id, attributes } = response.data.data;
            return { id, url: attributes.url };
        } catch (error) {
            if (axios.isAxiosError(error) && error.response) {
                return rejectWithValue(error.response?.data.message || 'Failed to generate image');
            }
            return rejectWithValue('An unexpected error occurred');
        }
    }
);
export const exportProject = createAsyncThunk<
    void,
    string,
    { rejectValue: string }
>(
    'project/exportProject',
    async (projectId, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get(`/projects/${projectId}/export`, {
                responseType: 'blob',
            });

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            window.open(url);
        } catch (error) {
            if (axios.isAxiosError(error) && error.response) {
                const axiosError = error as AxiosError<{ message: string }>;
                return rejectWithValue(axiosError.response?.data.message || 'Failed to export project');
            }
            return rejectWithValue('An unexpected error occurred');
        }
    }
);

export const shareProjectOnLinkedIn = createAsyncThunk<
    void,
    { projectId: string },
    { rejectValue: string }
>(
    'projects/shareOnLinkedIn',
    async ({ projectId }, { rejectWithValue }) => {
        try {
            console.log('shareProjectOnLinkedIn')
            const response = await axiosInstance.put(`/projects/${projectId}/share/linkedin`);

            console.log('Project shared successfully:', response.data);
        } catch (error) {
            if (axios.isAxiosError(error) && error.response) {
                const axiosError = error as AxiosError<{ message: string }>;
                return rejectWithValue(axiosError.response?.data.message || 'Failed to share project on LinkedIn');
            }
            return rejectWithValue('An unexpected error occurred');
        }
    }
);

const initialState: ProjectState = {
    currentProject: null,
    loading: false,
    error: null,
    isPreviewVisible:false,
    isPostButtonVisible:false,
    currentStep:1,
    isPromptBodyVisible:false,
    scriptData:null,
    isLoadingSharing: false,
};
const projectSlice = createSlice({
    name: 'project',
    initialState,
    reducers: {
        setCurrentProject(state, action: PayloadAction<ProjectState['currentProject']>) {
            state.currentProject = action.payload;
        },
        setIsPreviewVisible(state, action: PayloadAction<ProjectState['isPreviewVisible']>) {
            state.isPreviewVisible = action.payload;
        },
        setIsPostButtonVisible(state, action: PayloadAction<ProjectState['isPostButtonVisible']>) {
            state.isPostButtonVisible = action.payload;
        },
        setIsPromptBodyVisible(state, action: PayloadAction<ProjectState['isPromptBodyVisible']>) {
            state.isPromptBodyVisible = action.payload;
        },
        setCurrentStep(state, action: PayloadAction<ProjectState['currentStep']>) {
            state.currentStep = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(createProject.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(
                createProject.fulfilled,
                (state, action: PayloadAction<CurrentProject>) => {
                    state.loading = false;
                    state.currentProject = action.payload;
                }
            )
            .addCase(createProject.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(updateProject.pending, (state) => {
               // state.loading = true;
                state.error = null;
            })
            .addCase(updateProject.fulfilled, (state, action: PayloadAction<CurrentProject>) => {
              //  state.loading = false;
                state.currentProject = action.payload;
            })
            .addCase(updateProject.rejected, (state, action) => {
               // state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(uploadDocument.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(uploadDocument.fulfilled, (state, action: PayloadAction<DocumentData>) => {
                state.loading = false;
                state.currentProject = {...state.currentProject,documentId:action.payload.id}
            })
            .addCase(uploadDocument.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Unknown error occurred';
            })
            .addCase(uploadImage.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(uploadImage.fulfilled, (state, action: PayloadAction<ImageData>) => {
                state.loading = false;
                state.currentProject = {...state.currentProject,image:{id:action.payload.id,url:action.payload.url}}
            })
            .addCase(uploadImage.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Unknown error occurred';
            })
            .addCase(fetchProject.pending, (state) => {
                console.log('LOADING true')
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchProject.fulfilled, (state, action: PayloadAction<FetchProjectResult>) => {
                state.loading = false;
                console.log('action', action.payload);
                console.log('LOADING FALSE')
                state.currentProject = action.payload.project
                if(action.payload.script){
                    state.scriptData=action.payload.script
                }

            })
            .addCase(fetchProject.rejected, (state, action) => {
                state.loading = false;

                //state.error = action.payload || 'Unknown error occurred';
            })
            .addCase(generateScript.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(generateScript.fulfilled, (state, action: PayloadAction<ScriptData>) => {
                state.loading = false;
                state.scriptData = action.payload;
            })
            .addCase(generateScript.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to generate script';
            })
            .addCase(fetchImage.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchImage.fulfilled, (state, action: PayloadAction<ImageData>) => {
                state.loading = false;
                if (state.currentProject) {
                    console.log('action.payload',action.payload)
                    state.currentProject.image = action.payload;
                }
            })
            .addCase(fetchImage.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to fetch image';
            })
            .addCase(generateImage.pending, (state) => {
            state.loading = true;
            state.error = null;
             })
            .addCase(generateImage.fulfilled, (state, action: PayloadAction<ImageData>) => {
                state.loading = false;
                if (state.currentProject) {
                    state.currentProject.image = action.payload; // Обновляем текущий проект
                }
            })
            .addCase(generateImage.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to generate image';
            })
            .addCase(exportProject.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(exportProject.fulfilled, (state) => {
                state.loading = false;
                state.error = null;
            })
            .addCase(exportProject.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to export project';
            })
            .addCase(shareProjectOnLinkedIn.pending, (state) => {
                state.isLoadingSharing = true;
                state.error = null;
            })
            .addCase(shareProjectOnLinkedIn.fulfilled, (state) => {
                state.isLoadingSharing = false;
            })
            .addCase(shareProjectOnLinkedIn.rejected, (state, action) => {
                state.isLoadingSharing = false;
                state.error = action.payload || 'Failed to share project';
            });


    },

});
export const { setCurrentProject,setIsPreviewVisible,setIsPromptBodyVisible,setCurrentStep,setIsPostButtonVisible } = projectSlice.actions;
export default projectSlice.reducer;
